import { useEffect, useMemo, useRef, useState } from 'react';
import type { CenterstageVideo } from '../../api/centerstageApi';
import { useConceptCounts, useLibrary, useLibraryFilters } from '../../api/centerstageQueries';
import { AdminStatusMessage, useAdminNarrowViewport } from '../../admin-ui';
import { useClientContext } from '../../hooks/useClientContext';
import { CenterstageCategoryRow } from './CenterstageCategoryRow';
import { CenterstageConceptsModal } from '../../components/centerstage/CenterstageConceptsModal';
import { CenterstageFilterChips, type CenterstageFilterChip } from '../../components/centerstage/CenterstageFilterChips';
import { CenterstageFindBar } from '../../components/centerstage/CenterstageFindBar';
import { CenterstageVideoGrid } from '../../components/centerstage/CenterstageVideoGrid';
import { CenterstageVideoModal } from './CenterstageVideoModal';
import { CENTERSTAGE_COPY as COPY } from '../../components/centerstage/centerstageCopy';
import { createCenterstageStyles } from '../../components/centerstage/centerstageStyles';
import { useCenterstageFilters, type CenterstageFilterPatch } from './useCenterstageFilters';

/**
 * Centerstage: Emtrain's public video library, browsable by search,
 * category, concept, type and setting (all AND'd, held in the URL), with a
 * detail dialog that hands a video to Author with AI. The library is the same
 * in every content context; switching context clears the filters.
 */
export function CenterstagePage() {
  const narrow = useAdminNarrowViewport();
  const styles = useMemo(() => createCenterstageStyles({ narrow }), [narrow]);
  const { filters, query, update, clearAll } = useCenterstageFilters();
  const { clientContext } = useClientContext();
  const accountId = clientContext ? Number(clientContext.id) : null;

  const filtersQuery = useLibraryFilters();
  const library = useLibrary(query);
  const [conceptsOpen, setConceptsOpen] = useState(false);
  const [countsRequested, setCountsRequested] = useState(false);
  const countsQuery = useConceptCounts(countsRequested);
  const [openVideo, setOpenVideo] = useState<CenterstageVideo | null>(null);

  // The field shows the committed term whenever it changes (Enter, a chip, back, Show all).
  const [draft, setDraft] = useState(filters.q);
  useEffect(() => {
    setDraft(filters.q);
  }, [filters.q]);

  // A context switch clears the filters (useCenterstageFilters) and anything typed.
  const contextId = clientContext?.id ?? null;
  const previousContextId = useRef(contextId);
  useEffect(() => {
    if (previousContextId.current === contextId) return;
    previousContextId.current = contextId;
    setDraft('');
  }, [contextId]);

  const categories = useMemo(() => filtersQuery.data?.categories ?? [], [filtersQuery.data]);
  const concepts = useMemo(() => filtersQuery.data?.concepts ?? [], [filtersQuery.data]);
  const types = filtersQuery.data?.types ?? [];
  const settings = filtersQuery.data?.settings ?? [];
  const categoryNames = useMemo(() => new Map(categories.map((category) => [category.id, category.name])), [categories]);
  const conceptNames = useMemo(() => new Map(concepts.map((concept) => [concept.id, concept.name])), [concepts]);

  const pickConcept = (conceptId: number) => {
    setDraft('');
    update({ conceptId, q: '' });
  };

  const chips: CenterstageFilterChip[] = [];
  if (filters.q) {
    chips.push({ key: 'q', label: filters.q, onRemove: () => update({ q: '' }) });
  }
  // Names come from the filters read; until it answers there is nothing to label a chip with.
  if (filtersQuery.data && filters.categoryId !== null) {
    chips.push({
      key: 'category',
      label: categoryNames.get(filters.categoryId) ?? `Category ${filters.categoryId}`,
      onRemove: () => update({ categoryId: null }),
    });
  }
  if (filtersQuery.data && filters.conceptId !== null) {
    chips.push({
      key: 'concept',
      label: conceptNames.get(filters.conceptId) ?? `Concept ${filters.conceptId}`,
      onRemove: () => update({ conceptId: null }),
    });
  }
  const typeOption = types.find((option) => option.value === filters.type);
  if (typeOption) chips.push({ key: 'type', label: typeOption.label, onRemove: () => update({ type: null }) });
  const settingOption = settings.find((option) => option.value === filters.setting);
  if (settingOption) chips.push({ key: 'setting', label: settingOption.label, onRemove: () => update({ setting: null }) });

  const videos = useMemo(() => library.data?.pages.flatMap((page) => page.videos) ?? [], [library.data]);
  const gridStatus = library.isPending ? 'pending' : library.isError && !library.data ? 'error' : 'success';

  return (
    <div style={styles.page}>
      <div style={styles.wrap}>
        <div style={styles.sticky}>
          <div style={styles.titleBlock}>
            <h1 style={styles.title}>{COPY.title}</h1>
            <p style={styles.description}>{COPY.description}</p>
          </div>
          <CenterstageFindBar
            styles={styles}
            draft={draft}
            onDraftChange={setDraft}
            searchTerm={filters.q}
            onCommitSearch={(term) => update({ q: term })}
            concepts={concepts}
            categoryNames={categoryNames}
            onPickConcept={pickConcept}
            onBrowseConcepts={() => {
              setCountsRequested(true);
              setConceptsOpen(true);
            }}
            types={types}
            settings={settings}
            type={filters.type}
            setting={filters.setting}
            onTypeChange={(type) => update({ type })}
            onSettingChange={(setting) => update({ setting })}
          />
          {filtersQuery.isError ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <AdminStatusMessage tone="error">{COPY.filtersError}</AdminStatusMessage>
              <button type="button" onClick={() => void filtersQuery.refetch()} style={styles.secondaryButton}>{COPY.retry}</button>
            </div>
          ) : null}
          <CenterstageCategoryRow
            styles={styles}
            categories={categories}
            selectedId={filters.categoryId}
            onSelect={(categoryId) => update({ categoryId })}
          />
          <CenterstageFilterChips
            styles={styles}
            chips={chips}
            onShowAll={() => {
              setDraft('');
              clearAll();
            }}
          />
        </div>
        <CenterstageVideoGrid
          styles={styles}
          status={gridStatus}
          videos={videos}
          total={library.data?.pages[0]?.total ?? null}
          hasMore={library.hasNextPage}
          loadingMore={library.isFetchingNextPage}
          loadMoreFailed={library.isFetchNextPageError}
          onShowMore={() => void library.fetchNextPage()}
          onRetry={() => void library.refetch()}
          onOpen={setOpenVideo}
        />
      </div>
      <CenterstageVideoModal
        styles={styles}
        video={openVideo}
        accountId={accountId}
        onClose={() => setOpenVideo(null)}
        onFilter={(patch: CenterstageFilterPatch) => {
          setOpenVideo(null);
          update(patch);
        }}
      />
      <CenterstageConceptsModal
        styles={styles}
        isOpen={conceptsOpen}
        categories={categories}
        concepts={concepts}
        counts={countsQuery.data}
        countsStatus={countsQuery.isError ? 'error' : countsQuery.isSuccess ? 'success' : 'pending'}
        onPick={(conceptId) => {
          setConceptsOpen(false);
          setOpenVideo(null);
          pickConcept(conceptId);
        }}
        onClose={() => setConceptsOpen(false)}
      />
    </div>
  );
}
